import {
  Box,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettlementKpiItemStyles } from './settlementKpiItem.styles'

interface SettlementKpiItemProps {
  title: string
  value: number | string | undefined
  description?: string
  detail?: string | React.ReactNode
  leftFlex?: number
  rightFlex?: number
  marginRight?: string | number
  isLoading?: boolean
  /** Si se provee, la card es clickable */
  onClick?: () => void
  /** Estado activo - muestra la card con color primario */
  isActive?: boolean
  /** Identificador único para tracking del estado activo */
  id?: string
  disabled?: boolean
  tooltipString?: string
}

const useStyles = makeStyles(useSettlementKpiItemStyles)

export const SettlementKpiItem: React.FC<SettlementKpiItemProps> = ({
  title,
  value,
  description,
  detail,
  leftFlex = 6,
  rightFlex = 6,
  marginRight = '16px',
  isLoading = false,
  onClick,
  isActive = false,
  id,
  disabled = false,
  tooltipString
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements'])
  const theme = useTheme()
  const screenIsSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles(theme)

  // Determina si está deshabilitado (value es 0 o undefined)
  const isDisabled = useMemo(() => {
    if (isLoading) return false
    const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10)
    return isNaN(numericValue) || numericValue === 0 || disabled
  }, [value, isLoading, disabled])

  const isClickable = !!onClick && !isDisabled

  const handleClick = useCallback(() => {
    if (isClickable && onClick) {
      onClick()
    }
  }, [isClickable, onClick])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault()
        onClick?.()
      }
    },
    [isClickable, onClick]
  )

  return (
    <Tooltip
      title={isDisabled && tooltipString ? tooltipString : ''}
      disableHoverListener={!isDisabled && !!tooltipString}
    >
      <Paper
        elevation={0}
        className={clsx(
          classes.root,
          detail && classes.detailFullWidth,
          isClickable && classes.clickable,
          isActive && classes.active,
          isDisabled && classes.disabled
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        aria-pressed={isClickable ? isActive : undefined}
        aria-disabled={isDisabled && !!onClick ? true : undefined}
      >
        <Box className={classes.titleBox}>
          <Typography variant="subtitle2" className={classes.titleText}>
            {title}
          </Typography>
        </Box>
        <Box className={classes.contentRow}>
          <Box
            className={detail ? classes.leftContent : classes.leftContentFull}
            style={{
              flex: detail ? (leftFlex ? leftFlex : 1) : 1
            }}
          >
            {isLoading ? (
              <Skeleton
                variant="rect"
                width={screenIsSmall ? '80%' : '60%'}
                height={50}
                className={classes.skeleton}
              />
            ) : (
              <Typography className={classes.valueText} style={{ textAlign: 'right' }}>
                {value ?? 0}
              </Typography>
            )}
            {description && (
              <Typography className={classes.descriptionText} style={{ textAlign: 'right' }}>
                {description}
              </Typography>
            )}
          </Box>
          {detail && (
            <Box
              className={classes.rightContent}
              style={{
                flex: rightFlex ? rightFlex : 1
              }}
            >
              {isLoading ? (
                <Skeleton variant="text" width={screenIsSmall ? '80%' : '60%'} height={40} />
              ) : typeof detail === 'string' ? (
                <Typography
                  variant="subtitle2"
                  style={{ textAlign: 'left', whiteSpace: 'pre-line' }}
                >
                  {detail}
                </Typography>
              ) : (
                detail
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Tooltip>
  )
}
